import { useState } from "react";
import { Link } from "react-router-dom";
import { Sprout, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const DEMO_EMAIL = "demo@vegmentor.app";
const DISMISS_KEY = "vm_demo_banner_dismissed";

/**
 * Only visible when the active session is the auto-seeded demo account.
 * Offers a one-tap nudge to /register so visitors can convert into real users.
 * Dismissible per-tab via sessionStorage — reappears on the next visit.
 */
export default function DemoBanner() {
    const { user } = useAuth();
    const [dismissed, setDismissed] = useState(
        () => typeof window !== "undefined" && sessionStorage.getItem(DISMISS_KEY) === "1"
    );

    if (!user || user === false) return null;
    if (user.email !== DEMO_EMAIL) return null;
    if (dismissed) return null;

    const dismiss = () => {
        sessionStorage.setItem(DISMISS_KEY, "1");
        setDismissed(true);
    };

    return (
        <div
            data-testid="demo-banner"
            className="journal-card p-5 flex flex-wrap items-center gap-4 border border-[var(--vm-accent)]/40 bg-gradient-to-r from-[var(--vm-sage)]/8 to-[var(--vm-accent)]/8"
        >
            <div className="w-10 h-10 rounded-full bg-[var(--vm-accent)]/15 text-[var(--vm-accent)] flex items-center justify-center shrink-0">
                <Sprout className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-[14rem]">
                <p className="text-xs uppercase tracking-wider text-[var(--vm-sage)] mb-0.5">
                    You're exploring the demo garden
                </p>
                <p className="font-serif-display text-xl text-[var(--vm-text)] leading-snug">
                    Enjoying the tour? Build your own plot in under a minute.
                </p>
                <p className="text-sm text-[var(--vm-text-muted)] mt-1">
                    Your own garden keeps its history, notes, photos and AI chats private to you.
                </p>
            </div>
            <Link
                to="/register"
                data-testid="demo-banner-register-link"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-5 py-2 text-sm font-medium shrink-0"
            >
                Plant your first seed <ArrowRight className="w-4 h-4" />
            </Link>
            <button
                type="button"
                onClick={dismiss}
                data-testid="demo-banner-dismiss"
                className="text-[var(--vm-text-muted)] hover:text-[var(--vm-text)] p-1.5 rounded shrink-0"
                aria-label="Dismiss demo banner"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}
